import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Textarea from "@/v2-ui/textarea"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import LicenseAddOnCustomerSelector from "@/v2-backoffice-client/license/addOns/catalog/LicenseAddOnCustomerSelector"
import LicenseAddOnProductsField from "@/v2-backoffice-client/license/addOns/catalog/LicenseAddOnProductsField"
import { useAddOnUpsertMutation } from "@/v2-backoffice-client/license/addOns/catalog/license.addOn.upsert.mutation"
import { evictAddOnCatalogQueries } from "@/v2-backoffice-client/license/license.utils"
import type { AddOnCatalogFragment } from "@/v2-backoffice-client/license/addOns/catalog/__types__/license.addOn.catalog.fragment"

export type LicenseAddOnUpsertDialogProps = {
  /** Null/undefined = create a new add-on. */
  addOn?: AddOnCatalogFragment | null
  open: boolean
  onOpenChange(open: boolean): void
}

// Creates or edits an add-on — its name, which license it goes with, and
// its scope. Which packages the add-on gives is the add-on panel's Packages
// tab's job. Scope (shared catalog vs bespoke) locks once customers have the
// add-on: the API refuses the change with a readable error, and the hint
// below the picker says so up front.
function LicenseAddOnUpsertDialog(props: LicenseAddOnUpsertDialogProps) {
  const { addOn, open, onOpenChange } = props
  const isCreate = !addOn

  const [name, setName] = useState(addOn?.Name ?? "")
  const [code, setCode] = useState(addOn?.Code ?? "")
  const [description, setDescription] = useState(addOn?.Description ?? "")
  const [customerId, setCustomerId] = useState<number | null>(
    addOn?.CustomerId ?? null,
  )
  const [customerName, setCustomerName] = useState<string | null>(
    addOn?.CustomerName ?? null,
  )
  const [productCodes, setProductCodes] = useState<number[]>(
    addOn?.Products.map((product) => product.Code) ?? [],
  )

  const isScopeLocked = !isCreate && addOn.GrantCount > 0
  const { open: openViewSheet } = useAppViewSheetOpener()

  const [upsertAddOn, { loading, error }] = useAddOnUpsertMutation({
    update: evictAddOnCatalogQueries,
    onCompleted(data) {
      toast({
        variant: "success",
        title: isCreate ? "Add-on created" : "Add-on saved",
        description: isCreate
          ? `${data.upsertAddOn.Name} is in the catalog. Next: tick the packages it gives, on its Packages tab.`
          : `${data.upsertAddOn.Name} was updated.`,
      })
      if (isCreate) {
        setName("")
        setCode("")
        setDescription("")
        setCustomerId(null)
        setCustomerName(null)
        setProductCodes([])
        // Land on the new add-on's panel, on its Packages tab.
        openViewSheet("addOn", data.upsertAddOn.Id)
      }
      onOpenChange(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            {isCreate ? "New Add-on" : `Edit Add-on — ${addOn.Name}`}
          </Dialog.Title>
          <Dialog.Description>
            An add-on is something a customer buys on top of a license. Name it,
            say which license it goes with, and then tick what it gives on its
            Packages tab. What it costs stays in Fenerum.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              placeholder="e.g. API access"
              onChange={(e) => {
                setName(e.target.value)
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>Code</Label>
            <Input
              value={code}
              placeholder="e.g. API_ACCESS (optional for bespoke add-ons)"
              onChange={(e) => {
                setCode(e.target.value)
              }}
            />
            <p className="text-xs text-muted-foreground">
              Stable identifier, unique where present. Catalog add-ons should
              carry one; bespoke ones usually don&apos;t need it.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={description}
              placeholder="What this add-on gives the customer, in one sentence (optional)"
              onChange={(e) => {
                setDescription(e.target.value)
              }}
            />
          </div>

          <LicenseAddOnProductsField
            value={productCodes}
            onValueChange={setProductCodes}
          />

          <div className="space-y-2">
            <Label>Scope</Label>
            <LicenseAddOnCustomerSelector
              disabled={isScopeLocked}
              selectedCustomerId={customerId}
              onSelect={(customer) => {
                setCustomerId(customer?.Id ?? null)
                setCustomerName(customer?.Name ?? null)
              }}
            >
              {customerName ? `Bespoke — ${customerName}` : "Shared catalog"}
            </LicenseAddOnCustomerSelector>
            <p className="text-xs text-muted-foreground">
              {isScopeLocked
                ? `Scope is locked while ${addOn.GrantCount} customer(s) have this add-on — remove it from them first.`
                : "A shared catalog add-on can be added to any customer; a bespoke add-on only ever to its one customer."}
            </p>
          </div>
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={loading}
            disabled={!name.trim() || productCodes.length === 0}
            data-feature-code="UI_BackofficeAddOn_Upsert_Save"
            onClick={() => {
              upsertAddOn({
                variables: {
                  input: {
                    Id: addOn?.Id ?? null,
                    Code: code.trim() || null,
                    Name: name.trim(),
                    Description: description.trim() || null,
                    CustomerId: customerId,
                    ProductCodes: productCodes,
                  },
                },
              })
            }}
          >
            {loading ? "Saving..." : isCreate ? "Create Add-on" : "Save Add-on"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicenseAddOnUpsertDialog
